import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Home, DollarSign, Square, Bed, Bath, Car, Calendar, MapPin, X, Image, Award, Settings } from 'lucide-react';
import { UAD_VERSIONS } from '../utils/uadMapping';

const PropertyComparisonTable = ({ subjectProperty, comparableData = [], currentAddress = '' }) => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [visibleColumns] = useState(3); // Show 3 comparable properties at a time

  // Image gallery modal state
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [galleryTitle, setGalleryTitle] = useState('');

  // NEW UAD CONTROLS STATE - Added without affecting existing functionality
  const [showUADControls, setShowUADControls] = useState(false);
  const [uadVersion, setUadVersion] = useState(UAD_VERSIONS.UAD_36);
  const [maxComparables, setMaxComparables] = useState(12);
  const [uadCompliantOnly, setUadCompliantOnly] = useState(false);

  // NEW UAD FILTERING FUNCTION - Added without affecting existing logic
  const filterComparablesForUAD = (data) => {
    let filtered = [...data];

    // Filter by UAD compliance if enabled
    if (uadCompliantOnly) {
      filtered = filtered.filter(comp => {
        const compData = comp.rawData || comp;
        // Check if comparable has basic UAD required fields
        return compData.BedroomsTotal &&
               compData.BathroomsDecimal &&
               compData.LivingArea &&
               compData.YearBuilt &&
               compData.ClosePrice;
      });
    }

    // Apply max comparables limit
    return filtered.slice(0, maxComparables);
  };

  // Get comparable properties for display - ENHANCED with UAD filtering
  const filteredComparables = showUADControls ? filterComparablesForUAD(comparableData) : comparableData;
  const comparables = filteredComparables.slice(0, showUADControls ? maxComparables : 12);
  const totalPages = Math.ceil(comparables.length / visibleColumns);
  const currentPage = Math.floor(scrollPosition / visibleColumns);

  const scrollLeft = () => {
    if (scrollPosition > 0) {
      setScrollPosition(scrollPosition - visibleColumns);
    }
  };

  const scrollRight = () => {
    if (scrollPosition + visibleColumns < comparables.length) {
      setScrollPosition(scrollPosition + visibleColumns);
    }
  };

  const visibleComparables = comparables.slice(scrollPosition, scrollPosition + visibleColumns);

  // Gallery functions
  const openGallery = (images, title, startIndex = 0) => {
    setGalleryImages(images);
    setGalleryTitle(title);
    setCurrentImageIndex(startIndex);
    setIsGalleryOpen(true);
  };

  const closeGallery = () => {
    setIsGalleryOpen(false);
    setGalleryImages([]);
    setCurrentImageIndex(0);
    setGalleryTitle('');
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getPropertyValue = (property, field) => {
    if (!property) return 'N/A';

    switch (field) {
      case 'address':
        return property.address || property.UnparsedAddress || property.Address || currentAddress || 'N/A';
      case 'beds':
        // Support both transformed data (beds) and raw PropMix data (BedroomsTotal)
        return property.beds || property.BedroomsTotal || 'N/A';
      case 'baths':
        // Support both transformed data (baths) and raw PropMix data (BathroomsDecimal, etc.)
        return property.baths || property.BathroomsDecimal || property.BathroomsFull || property.BathroomsTotalInteger || 'N/A';
      case 'sqft':
        // Support both transformed data (livingArea) and raw PropMix data (LivingArea)
        const sqft = property.livingArea || property.LivingArea;
        return sqft ? `${parseInt(sqft).toLocaleString()}` : 'N/A';
      case 'yearBuilt':
        return property.yearBuilt || property.YearBuilt || 'N/A';
      case 'garage':
        return property.garageSpaces || property.GarageSpaces || property.ParkingSpaces || 'N/A';
      case 'listPrice':
        // Support both transformed data (formatted listPrice) and raw PropMix data (ListPrice)
        if (property.listPrice && typeof property.listPrice === 'string') {
          return property.listPrice; // Already formatted
        }
        return property.ListPrice ? formatCurrency(property.ListPrice) : 'N/A';
      case 'salePrice':
        // Support both transformed data (formatted salePrice) and raw PropMix data (ClosePrice)
        if (property.salePrice && typeof property.salePrice === 'string') {
          return property.salePrice; // Already formatted
        }
        return property.ClosePrice ? formatCurrency(property.ClosePrice) : 'N/A';
      case 'pricePerSqft':
        // Support both transformed data (formatted pricePerSqft) and raw PropMix data (PriceperSquareFeet)
        if (property.pricePerSqft && typeof property.pricePerSqft === 'string') {
          return property.pricePerSqft; // Already formatted
        }
        return property.PriceperSquareFeet ? `$${property.PriceperSquareFeet}` : 'N/A';
      case 'saleDate':
        return property.date || (property.CloseDate ? formatDate(property.CloseDate) : 'N/A');
      case 'dom':
        const dom = property.daysOnMarket || property.DaysOnMarket;
        return dom ? `${dom} days` : 'N/A';
      case 'lotSize':
        const lotSize = property.lotSize || property.LotSizeSquareFeet;
        return lotSize ? `${parseInt(lotSize).toLocaleString()} sq ft` : 'N/A';
      case 'propertyType':
        return property.style || property.PropertySubType || property.PropertyType || 'N/A';
      case 'heating':
        return property.heating || property.Heating || 'N/A';
      case 'cooling':
        return property.cooling || property.Cooling || 'N/A';
      case 'stories':
        return property.stories || property.StoriesTotal || property.DerivedStories || 'N/A';
      case 'fireplaces':
        return property.fireplaces || property.FireplacesTotal || property.FireplaceTotal || '0';
      case 'mlsNumber':
        return property.mlsNumber || property.MlsListingNumber || property.MLSNumber || 'N/A';
      default:
        return 'N/A';
    }
  };

  const comparisonRows = [
    { label: 'Address', field: 'address', icon: Home },
    { label: 'MLS Number', field: 'mlsNumber', icon: Home },
    { label: 'Property Type', field: 'propertyType', icon: Home },
    { label: 'Sale Date', field: 'saleDate', icon: Calendar },
    { label: 'Sale Price', field: 'salePrice', icon: DollarSign },
    { label: 'List Price', field: 'listPrice', icon: DollarSign },
    { label: 'Price per Sq Ft', field: 'pricePerSqft', icon: DollarSign },
    { label: 'Bedrooms', field: 'beds', icon: Bed },
    { label: 'Bathrooms', field: 'baths', icon: Bath },
    { label: 'Living Area (Sq Ft)', field: 'sqft', icon: Square },
    { label: 'Lot Size', field: 'lotSize', icon: MapPin },
    { label: 'Year Built', field: 'yearBuilt', icon: Calendar },
    { label: 'Stories', field: 'stories', icon: Home },
    { label: 'Garage Spaces', field: 'garage', icon: Car },
    { label: 'Fireplaces', field: 'fireplaces', icon: Home },
    { label: 'Days on Market', field: 'dom', icon: Calendar },
    { label: 'Heating', field: 'heating', icon: Home },
    { label: 'Cooling', field: 'cooling', icon: Home },
    { label: 'Property Images', field: 'images', icon: Home, isImages: true }
  ];

  if (!subjectProperty && comparables.length === 0) {
    return (
      <div className="text-center py-12">
        <Home className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400 text-lg mb-2">No Properties to Compare</p>
        <p className="text-gray-500 text-sm">
          Enter a property address to start comparing properties.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
      {/* Header with Navigation */}
      <div className="bg-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white flex items-center gap-2">
            <Home className="w-6 h-6 text-blue-400" />
            Property Comparison
            {/* NEW UAD INDICATOR - Added without modifying existing title */}
            {showUADControls && (
              <span className="ml-2 px-2 py-1 text-xs bg-green-600 text-white rounded">
                UAD {uadVersion}
              </span>
            )}
          </h3>
          <div className="flex items-center gap-4">
            <span className="text-gray-300 text-sm">
              Showing {Math.min(visibleColumns, comparables.length)} of {comparables.length} comparables
            </span>

            {/* NEW UAD CONTROLS TOGGLE - Added without affecting existing controls */}
            <button
              onClick={() => setShowUADControls(!showUADControls)}
              className={`flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors ${
                showUADControls
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
              }`}
            >
              <Award className="w-4 h-4" />
              UAD Controls
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={scrollLeft}
                disabled={scrollPosition === 0}
                className="p-2 bg-gray-600 text-white rounded hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-gray-300 text-sm">
                {currentPage + 1} / {Math.max(1, totalPages)}
              </span>
              <button
                onClick={scrollRight}
                disabled={scrollPosition + visibleColumns >= comparables.length}
                className="p-2 bg-gray-600 text-white rounded hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* NEW UAD CONTROLS PANEL - Added without affecting existing layout */}
        {showUADControls && (
          <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-green-400" />
                <h4 className="text-lg font-semibold text-white">UAD Comparison Settings</h4>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-300">Version:</span>
                <select
                  value={uadVersion}
                  onChange={(e) => setUadVersion(e.target.value)}
                  className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                >
                  <option value={UAD_VERSIONS.UAD_26}>UAD 2.6</option>
                  <option value={UAD_VERSIONS.UAD_36}>UAD 3.6</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Max Comparables Control */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Maximum Comparables
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min="3"
                    max="20"
                    value={maxComparables}
                    onChange={(e) => setMaxComparables(parseInt(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-white font-medium w-8 text-center">
                    {maxComparables}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  UAD recommends 3-6 comparables for most appraisals
                </p>
              </div>

              {/* UAD Compliance Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Filter Options
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="uadCompliant"
                    checked={uadCompliantOnly}
                    onChange={(e) => setUadCompliantOnly(e.target.checked)}
                    className="rounded"
                  />
                  <label htmlFor="uadCompliant" className="text-sm text-gray-300">
                    UAD Compliant Only
                  </label>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Show only properties with complete UAD data
                </p>
              </div>

              {/* Statistics */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Comparison Stats
                </label>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Total Available:</span>
                    <span className="text-white">{comparableData.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Currently Shown:</span>
                    <span className="text-green-400">{comparables.length}</span>
                  </div>
                  {uadCompliantOnly && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">UAD Compliant:</span>
                      <span className="text-blue-400">
                        {filterComparablesForUAD(comparableData).length}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* UAD Guidelines */}
            <div className="mt-4 p-3 bg-blue-900 border border-blue-700 rounded">
              <h6 className="text-sm font-medium text-blue-200 mb-2">UAD Best Practices</h6>
              <ul className="text-xs text-blue-300 space-y-1">
                <li>• Use 3-6 comparable sales for most residential appraisals</li>
                <li>• Ensure all comparables have complete UAD-required data fields</li>
                <li>• Select properties most similar to subject in location, size, and characteristics</li>
                <li>• Document any significant differences requiring adjustments</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Comparison Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="text-left p-4 text-gray-300 font-medium border-r border-gray-600 sticky left-0 bg-gray-700 z-10 min-w-[200px]">
                Property Details
              </th>
              {/* Subject Property Header - Always Visible */}
              <th className="text-center p-4 text-white font-medium border-r border-gray-600 bg-blue-700 min-w-[200px]">
                <div className="flex flex-col items-center gap-2">
                  <Home className="w-5 h-5" />
                  <span className="text-sm">Subject Property</span>
                  <span className="text-xs text-blue-100">{currentAddress}</span>
                </div>
              </th>
              {/* Comparable Properties Headers */}
              {visibleComparables.map((comp, index) => (
                <th key={`comp-header-${comp.mlsNumber || comp.address || index}-${scrollPosition}`} className="text-center p-4 text-white font-medium border-r border-gray-600 bg-gray-700 min-w-[200px]">
                  <div className="flex flex-col items-center gap-2">
                    <Home className="w-5 h-5 text-green-400" />
                    <span className="text-sm">Comparable {scrollPosition + index + 1}</span>
                    <span className="text-xs text-gray-300 truncate max-w-[180px]" title={comp.address}>
                      {comp.address}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {comparisonRows.map((row, rowIndex) => {
              const Icon = row.icon;
              return (
                <tr key={`comparison-row-${row.field}-${rowIndex}`} className={`border-t border-gray-700 ${rowIndex % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'}`}>
                  {/* Row Label - Always Visible */}
                  <td className="p-4 text-gray-300 font-medium border-r border-gray-600 sticky left-0 bg-inherit z-10">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      {row.label}
                    </div>
                  </td>
                  
                  {/* Subject Property Data */}
                  <td className="p-4 text-center text-white border-r border-gray-600 bg-blue-900/20">
                    {row.isImages ? (
                      <div className="flex flex-wrap gap-2 justify-center">
                        {(() => {
                          // Support both transformed data (photos) and raw PropMix data (ImageURLs)
                          const images = subjectProperty?.photos || subjectProperty?.ImageURLs || [];
                          return images.length > 0 ? (
                            <>
                              {images.slice(0, 3).map((image, imgIndex) => (
                                <img
                                  key={imgIndex}
                                  src={image}
                                  alt={`Subject property ${imgIndex + 1}`}
                                  className="w-20 h-16 object-cover rounded border border-gray-500 cursor-pointer hover:opacity-80 transition-opacity"
                                  onError={(e) => e.target.style.display = 'none'}
                                  onClick={() => openGallery(images, `Subject Property - ${currentAddress}`, imgIndex)}
                                />
                              ))}
                              {images.length > 3 && (
                                <div
                                  className="w-20 h-16 bg-gray-600 rounded border border-gray-500 flex items-center justify-center cursor-pointer hover:bg-gray-500 transition-colors"
                                  onClick={() => openGallery(images, `Subject Property - ${currentAddress}`, 0)}
                                >
                                  <span className="text-xs text-white">+{images.length - 3}</span>
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400 text-sm">No images</span>
                          );
                        })()}
                      </div>
                    ) : (
                      <span className="font-medium">
                        {getPropertyValue(subjectProperty, row.field)}
                      </span>
                    )}
                  </td>
                  
                  {/* Comparable Properties Data */}
                  {visibleComparables.map((comp, index) => {
                    const compImages = comp.rawData?.ImageURLs || comp.ImageURLs || [];
                    const compAddress = comp.address || getPropertyValue(comp.rawData || comp, 'address');
                    
                    return (
                      <td key={`comp-data-${rowIndex}-${comp.mlsNumber || comp.address || index}-${scrollPosition}`} className="p-4 text-center text-gray-200 border-r border-gray-600">
                        {row.isImages ? (
                          <div className="flex flex-wrap gap-2 justify-center">
                            {compImages && compImages.length > 0 ? (
                              <>
                                {compImages.slice(0, 3).map((image, imgIndex) => (
                                  <img
                                    key={imgIndex}
                                    src={image}
                                    alt={`Comparable ${index + 1} image ${imgIndex + 1}`}
                                    className="w-20 h-16 object-cover rounded border border-gray-500 cursor-pointer hover:opacity-80 transition-opacity"
                                    onError={(e) => e.target.style.display = 'none'}
                                    onClick={() => openGallery(compImages, `Comparable ${scrollPosition + index + 1} - ${compAddress}`, imgIndex)}
                                  />
                                ))}
                                {compImages.length > 3 && (
                                  <div 
                                    className="w-20 h-16 bg-gray-600 rounded border border-gray-500 flex items-center justify-center cursor-pointer hover:bg-gray-500 transition-colors"
                                    onClick={() => openGallery(compImages, `Comparable ${scrollPosition + index + 1} - ${compAddress}`, 0)}
                                  >
                                    <span className="text-xs text-white">+{compImages.length - 3}</span>
                                  </div>
                                )}
                              </>
                            ) : (
                              <span className="text-gray-400 text-sm">No images</span>
                            )}
                          </div>
                        ) : (
                        <span className={`${
                          row.field === 'salePrice' || row.field === 'listPrice' ? 'font-semibold text-green-400' :
                          row.field === 'pricePerSqft' ? 'font-semibold text-purple-400' :
                          'font-medium'
                        }`}>
                          {getPropertyValue(comp.rawData || comp, row.field)}
                        </span>
                      )}
                    </td>
                    );
                  })}
                </tr>
              );
            })}
            
            {/* Similarity Score Row */}
            <tr className="border-t border-gray-700 bg-gray-700">
              <td className="p-4 text-gray-300 font-medium border-r border-gray-600 sticky left-0 bg-gray-700 z-10">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Similarity Score
                </div>
              </td>
              <td className="p-4 text-center text-white border-r border-gray-600 bg-blue-900/20">
                <span className="px-3 py-1 bg-blue-600 text-white text-sm rounded-full">
                  100% (Subject)
                </span>
              </td>
              {visibleComparables.map((comp, index) => (
                <td key={`comp-similarity-${comp.mlsNumber || comp.address || index}-${scrollPosition}`} className="p-4 text-center border-r border-gray-600">
                  <span className={`px-3 py-1 text-white text-sm rounded-full ${
                    comp.similarityScore >= 85 ? 'bg-green-600' :
                    comp.similarityScore >= 75 ? 'bg-blue-600' :
                    comp.similarityScore >= 65 ? 'bg-yellow-600' : 'bg-gray-600'
                  }`}>
                    {comp.similarityScore || 'N/A'}%
                  </span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Footer with Summary */}
      <div className="bg-gray-700 p-4 border-t border-gray-600">
        <div className="flex items-center justify-between text-sm text-gray-300">
          <span>
            Comparing subject property with {comparables.length} comparable properties
          </span>
          <span>
            Use arrows to scroll through all comparables
          </span>
        </div>
      </div>

      {/* Image Gallery Modal */}
      {isGalleryOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
          <div className="max-w-4xl w-full max-h-full flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between text-white mb-4">
              <h3 className="text-xl font-semibold">{galleryTitle}</h3>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-300">
                  {currentImageIndex + 1} of {galleryImages.length}
                </span>
                <button
                  onClick={closeGallery}
                  className="p-2 hover:bg-gray-700 rounded-full transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* Image Display */}
            <div className="relative flex-1 flex items-center justify-center">
              {galleryImages.length > 0 && (
                <>
                  <img
                    src={galleryImages[currentImageIndex]}
                    alt={`Property image ${currentImageIndex + 1}`}
                    className="max-w-full max-h-[70vh] object-contain rounded-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  
                  {/* Navigation Buttons */}
                  {galleryImages.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
            
            {/* Thumbnail Strip */}
            {galleryImages.length > 1 && (
              <div className="flex justify-center gap-2 mt-4 overflow-x-auto pb-2">
                {galleryImages.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className={`w-16 h-12 object-cover rounded cursor-pointer transition-all ${
                      index === currentImageIndex 
                        ? 'border-2 border-blue-500 opacity-100' 
                        : 'border border-gray-500 opacity-60 hover:opacity-80'
                    }`}
                    onClick={() => setCurrentImageIndex(index)}
                    onError={(e) => e.target.style.display = 'none'}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyComparisonTable;